import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { getIcon } from "@sylviq/ui-react-web/extensions/icons";
import { type ReactNode, useEffect, useState } from "react";
import { iconForCrate } from "@/features/launcher/icon-for-crate";
import { personalApps } from "@/features/launcher/personal-apps";
import {
  type CrateSummary,
  installCrate,
  listCrates,
  listLegalEntities,
  listOrganizations,
} from "@/lib/api";
import { APP_CATEGORIES, useAppCategory } from "@/lib/app-category";
import { BusinessEntitlementProvider, useBusinessEntitlement } from "@/lib/entitlement";
import { useSession } from "@/lib/session";
import { OrganizationNavigation, type ConsoleOrganizationOption } from "@/features/organizations/OrganizationNavigation";
import { savedOrganizationId, saveOrganizationId } from "@/features/organizations/selected-organization";

// Accounting is the one business app with a real, deployed frontend today.
// The crate catalog also carries backend modules that are not standalone
// apps; showing them as "coming soon" tiles advertises products we do not
// offer yet, so only this key reaches the launcher.
const BUSINESS_APP_KEYS = new Set(["accounting"]);

function launchUrl(
  crate: CrateSummary,
  organizationId: string,
): string | null {
  if (crate.key !== "accounting") return null;
  const origin = import.meta.env.VITE_NICHE_ACCOUNTING_ORIGIN ?? "";
  const url = new URL(origin || window.location.origin);
  url.searchParams.set("organization_id", organizationId);
  return url.toString();
}

interface LoadedCrates {
  organizationId: string;
  crates: CrateSummary[];
  hasLegalEntity: boolean;
}

async function loadBusinessCrates(
  organizationId: string,
): Promise<Omit<LoadedCrates, "organizationId">> {
  const [listed, entities] = await Promise.all([
    listCrates(organizationId),
    listLegalEntities(organizationId),
  ]);
  let crates = listed.filter((crate) => BUSINESS_APP_KEYS.has(crate.key));
  const toInstall = crates.filter(
    (crate) => crate.access === "crate_not_installed",
  );
  if (toInstall.length > 0) {
    await Promise.all(
      toInstall.map((crate) => installCrate(crate.key, organizationId)),
    );
    crates = (await listCrates(organizationId)).filter((crate) =>
      BUSINESS_APP_KEYS.has(crate.key),
    );
  }
  return { crates, hasLegalEntity: entities.length > 0 };
}

export function LauncherPage() {
  const { state } = useSession();
  const { category, setCategory } = useAppCategory();
  const showBusiness = category !== "security";
  const showSecurity = category !== "business";
  const [organizations, setOrganizations] = useState<ConsoleOrganizationOption[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [organizationsError, setOrganizationsError] = useState<string | null>(null);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    setOrganizationsLoading(true);
    setOrganizationsError(null);
    void listOrganizations()
      .then((items) => {
        if (cancelled) return;
        const options = items.map((organization) => ({
          id: organization.id,
          name: organization.name,
          canManageSecrets: organization.can_manage_secrets,
        }));
        setOrganizations(options);
        setOrganizationId((current) => {
          const saved = savedOrganizationId();
          return (
            options.find((organization) => organization.id === current)?.id ??
            options.find((organization) => organization.id === saved)?.id ??
            options[0]?.id ??
            null
          );
        });
      })
      .catch((cause) => {
        if (!cancelled) {
          setOrganizationsError(
            cause instanceof Error ? cause.message : String(cause),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setOrganizationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state.status === "authenticated" ? state.user.id : null, state.status]);

  const selectOrganization = (nextOrganizationId: string) => {
    saveOrganizationId(nextOrganizationId);
    setOrganizationId(nextOrganizationId);
  };

  if (state.status !== "authenticated") return null;

  const navigation = (
    <OrganizationNavigation
      organizationId={showBusiness ? organizationId ?? undefined : undefined}
      organizations={showBusiness ? organizations : undefined}
      onOrganizationChange={selectOrganization}
      view="apps"
    >
      <div className="space-y-10">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight">
            Your apps
          </h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {category === "security"
              ? "Security apps for your account."
              : category === "business"
                ? "Business apps for the selected organization."
                : "Business and security apps available to this organization and account."}
          </p>
          <label className="mt-4 grid max-w-sm gap-1 text-sm font-medium">
            Apps
            <select
              aria-label="App category"
              className="input"
              value={category}
              onChange={(event) => {
                const next = APP_CATEGORIES.find((option) => option === event.target.value);
                if (next) setCategory(next);
              }}
            >
              <option value="all">All</option>
              <option value="business">Business</option>
              <option value="security">Security</option>
            </select>
          </label>
        </div>

        {showBusiness ? (
          <section aria-labelledby="business-apps-heading">
            <h2
              id="business-apps-heading"
              className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]"
            >
              Business
            </h2>
            {organizationsLoading ? (
              <AppGridSkeleton />
            ) : organizationsError ? (
              <BusinessUnavailableCard
                title="Organizations could not be loaded"
                detail="Reload the page to try again."
              />
            ) : !organizationId ? (
              <BusinessUnavailableCard
                title="No organization yet"
                detail="Create one to open Business apps and manage legal entities."
                showPricing={false}
                action={
                  <a
                    className="mt-3 inline-block text-sm font-medium text-[var(--action)]"
                    href="/organizations/new"
                  >
                    Create organization
                  </a>
                }
              />
            ) : (
              <BusinessEntitlementProvider
                key={organizationId}
                organizationId={organizationId}
              >
                <LauncherBusinessApps organizationId={organizationId} />
              </BusinessEntitlementProvider>
            )}
          </section>
        ) : null}

        {showSecurity ? (
          <section aria-labelledby="security-apps-heading">
            <h2
              id="security-apps-heading"
              className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]"
            >
              Security
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {personalApps().map((app) => (
                <PersonalAppTile key={app.key} app={app} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </OrganizationNavigation>
  );

  return navigation;
}

function LauncherBusinessApps({ organizationId }: { organizationId: string }) {
  const entitlement = useBusinessEntitlement();
  const [loaded, setLoaded] = useState<LoadedCrates | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (entitlement.loading || !entitlement.business) return;
    let cancelled = false;
    setLoaded(null);
    setError(null);
    void loadBusinessCrates(organizationId)
      .then((result) => {
        if (!cancelled) setLoaded({ organizationId, ...result });
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : String(cause));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, entitlement.loading, entitlement.business]);

  if (entitlement.loading) return <AppGridSkeleton />;
  if (!entitlement.business) {
    return <BusinessUnavailableCard title="Business is a paid plan" detail="Business apps open this organization's legal entities, Extensions, and Developer access." />;
  }
  if (error) return <p className="text-sm text-[var(--danger)]">{error}</p>;
  if (!loaded) return <AppGridSkeleton />;
  if (!loaded.hasLegalEntity) {
    return (
      <BusinessUnavailableCard
        title="Add a legal entity first"
        detail="Accounting keeps books for a legal entity. Add the person or organization this organization manages, then open the app."
        showPricing={false}
        action={
          <a
            className="mt-3 inline-block text-sm font-medium text-[var(--action)]"
            href={`/organizations/${encodeURIComponent(organizationId)}/entities`}
          >
            Add legal entity
          </a>
        }
      />
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {loaded.crates.map((crate) => (
        <CrateTile
          key={crate.key}
          crate={crate}
          organizationId={organizationId}
        />
      ))}
    </div>
  );
}

function AppGridSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="surface h-32 animate-pulse rounded-2xl" />
      ))}
    </div>
  );
}

function BusinessUnavailableCard({
  title,
  detail,
  showPricing = true,
  action,
}: {
  title: string;
  detail: string;
  showPricing?: boolean;
  action?: ReactNode;
}) {
  return (
    <div className="surface rounded-2xl p-5">
      <div className="text-base font-semibold">{title}</div>
      <p className="mt-1 text-sm text-[var(--muted-foreground)]">{detail}</p>
      {showPricing ? (
        <a
          className="mt-3 inline-block text-sm font-medium text-[var(--action)]"
          href="/pricing"
        >
          See pricing
        </a>
      ) : null}
      {action}
    </div>
  );
}

function PersonalAppTile({
  app,
}: {
  app: ReturnType<typeof personalApps>[number];
}) {
  const Icon = app.icon;
  return (
    <a
      href={app.href}
      className="surface block rounded-2xl p-5 transition hover:bg-[var(--muted)]"
    >
      <span className="grid size-10 place-items-center rounded-xl bg-[var(--action)] text-[var(--action-foreground)]">
        <Icon className="size-5" />
      </span>
      <div className="mt-4 text-base font-semibold">{app.name}</div>
      <p className="mt-1 text-sm text-[var(--muted-foreground)]">
        {app.summary}
      </p>
    </a>
  );
}

function CrateTile({
  crate,
  organizationId,
}: {
  crate: CrateSummary;
  organizationId: string;
}) {
  const Icon = getIcon(iconForCrate(crate));
  const launchable =
    BUSINESS_APP_KEYS.has(crate.key) && crate.access === "available";
  const href = launchable ? launchUrl(crate, organizationId) : null;
  const unavailableLabel =
    crate.key === "accounting" && crate.access === "entitlement_required"
      ? "Access required"
      : crate.key === "accounting" && crate.access === "crate_not_installed"
        ? "Setup required"
        : "Unavailable";

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-[var(--action)] text-[var(--action-foreground)]">
          <Icon className="size-5" />
        </span>
        {!launchable ? <Badge tone="neutral">{unavailableLabel}</Badge> : null}
      </div>
      <div className="mt-4 text-base font-semibold">{crate.display_name}</div>
      <p className="mt-1 text-sm text-[var(--muted-foreground)]">
        {crate.summary}
      </p>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className="surface block rounded-2xl p-5 transition hover:bg-[var(--muted)]"
      >
        {body}
      </a>
    );
  }

  return (
    <div className="surface rounded-2xl p-5 opacity-60" aria-disabled="true">
      {body}
    </div>
  );
}
